import React, { useMemo, useState } from 'react';
import type { CombatActionTarget, CombatCalculation, CombatFightSummary, CombatLogEntry } from '../types';

interface CombatLogDrawerProps {
  open: boolean;
  entries: CombatLogEntry[];
  fights?: CombatFightSummary[];
  onClose: () => void;
}

function kindLabel(kind?: string) {
  if (kind === 'heal') return 'Heilung';
  if (kind === 'mana') return 'Mana';
  if (kind === 'effect') return 'Effekt';
  if (kind === 'system') return 'System';
  return 'Schaden';
}

function actionTypeLabel(type?: string) {
  if (type === 'spell') return 'Zauber';
  if (type === 'item') return 'Item';
  if (type === 'ability') return 'Fähigkeit';
  if (type === 'effect') return 'Effekt';
  if (type === 'attack') return 'Angriff';
  if (type === 'system') return 'System';
  if (type === 'flee') return 'Flucht';
  return type || 'Aktion';
}

function formatTime(iso?: string) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(iso?: string) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function targetLine(target: CombatActionTarget) {
  const bits: string[] = [target.name];
  if (typeof target.incoming === 'number' && target.kind === 'damage') {
    bits.push(`Roh ${target.incoming}`);
  }
  if (target.absorbed_buff) bits.push(`Buff ${target.absorbed_buff}`);
  if (target.absorbed_armor) bits.push(`Rüstung ${target.absorbed_armor}`);
  if (target.broken?.length) {
    bits.push(target.broken.map((part) => `${part.name || 'Rüstung'} kaputt`).join(', '));
  }
  if (typeof target.taken === 'number' && (target.kind === 'damage' || target.taken > 0)) {
    bits.push(target.absorbed_buff || target.absorbed_armor ? `Leben ${target.taken}` : `→ ${target.taken} nach Rüstung`);
  }
  if (typeof target.armor === 'number') {
    bits.push(`Rüstung ${target.armor}`);
  }
  bits.push(`HP ${target.health}/${target.max_health}`);
  if (target.effect) {
    bits.push(`+ ${target.effect}`);
  }
  return bits.join(' · ');
}

function CalculationBlock({ calculation, targets }: { calculation?: CombatCalculation; targets?: CombatActionTarget[] }) {
  const steps = calculation?.steps || [];
  return (
    <div className="combat-log-calc">
      {steps.length > 0 && (
        <ul className="combat-log-steps">
          {steps.map((step, index) => (
            <li key={`${step.label}-${index}`}>
              <strong>{step.label}</strong>
              {step.detail ? <span className="combat-log-step-detail">{step.detail}</span> : null}
              {step.value !== undefined && step.value !== null ? (
                <span className="combat-log-step-value">{step.value}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {calculation?.armor_formula && (
        <p className="combat-log-formula">
          Rüstungsformel ({calculation.armor_slot || '—'}): <code>{calculation.armor_formula}</code>
        </p>
      )}
      {(targets || []).map((target, index) => (
        <p key={`${target.name}-${index}`} className="combat-log-target-detail">
          {targetLine(target)}
          {target.armor_formula && !calculation?.armor_formula ? (
            <> · Formel: <code>{target.armor_formula}</code></>
          ) : null}
        </p>
      ))}
    </div>
  );
}

function LogEntryCard({ entry }: { entry: CombatLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const targetNames = (entry.targets || []).map(t => t.name).join(', ');
  const isSystem = entry.action_type === 'system' || entry.kind === 'system';
  const hasCalc = !isSystem && Boolean(
    (entry.calculation?.steps && entry.calculation.steps.length > 0)
    || (entry.targets && entry.targets.length > 0),
  );
  const summary = entry.summary || kindLabel(entry.kind);
  const mainLine = isSystem
    ? entry.action_label
    : [entry.actor_name, entry.action_label, targetNames || null].filter(Boolean).join(' → ');

  const toggle = () => {
    if (!hasCalc) return;
    setExpanded(open => !open);
  };

  return (
    <article className={`combat-log-entry is-${entry.kind || 'damage'}${isSystem ? ' is-system' : ''}${expanded ? ' is-open' : ''}`}>
      <button
        type="button"
        className="combat-log-entry-row"
        onClick={toggle}
        disabled={!hasCalc}
        aria-expanded={hasCalc ? expanded : undefined}
        aria-label={hasCalc ? (expanded ? 'Berechnung ausblenden' : 'Berechnung zeigen') : undefined}
      >
        <span className="combat-log-entry-time">{formatTime(entry.created_at)}</span>
        <span className="combat-log-entry-type">{actionTypeLabel(entry.action_type)}</span>
        <span className="combat-log-entry-body">
          <span className="combat-log-entry-main">{mainLine}</span>
          {summary ? <span className="combat-log-entry-summary">{summary}</span> : null}
        </span>
        {hasCalc ? (
          <span className="combat-log-entry-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
        ) : (
          <span className="combat-log-entry-chevron is-spacer" aria-hidden />
        )}
      </button>
      {hasCalc && expanded && (
        <CalculationBlock calculation={entry.calculation} targets={entry.targets} />
      )}
    </article>
  );
}

function fightMeta(entry: CombatLogEntry, fights: CombatFightSummary[]) {
  const fight = fights.find(item => item.id === entry.combat_id);
  if (fight) return fight;
  if (!entry.combat_id) return null;
  return {
    id: entry.combat_id,
    status: entry.combat_status || '',
    started_at: entry.combat_started_at,
    event_count: 0,
    number: entry.combat_id,
    label: `Kampf #${entry.combat_id}`,
  } as CombatFightSummary;
}

export function CombatLogDrawer({ open, entries, fights = [], onClose }: CombatLogDrawerProps) {
  const groups = useMemo(() => {
    const byCombat = new Map<number, { fight: CombatFightSummary | null; entries: CombatLogEntry[] }>();
    for (const entry of entries) {
      const combatId = entry.combat_id || 0;
      if (!byCombat.has(combatId)) {
        byCombat.set(combatId, { fight: fightMeta(entry, fights), entries: [] });
      }
      byCombat.get(combatId)!.entries.push(entry);
    }
    return Array.from(byCombat.entries()).map(([combatId, group]) => ({
      combatId,
      fight: group.fight || fights.find(item => item.id === combatId) || null,
      entries: group.entries,
    }));
  }, [entries, fights]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="combat-log-backdrop"
        data-gamepad-overlay-backdrop="log"
        data-gamepad-ignore
        aria-label="Kampflog schließen"
        onClick={onClose}
      />
      <aside
        id="combat-log-drawer"
        className="combat-log-drawer"
        data-testid="combat-log-drawer"
        data-gamepad-overlay="log"
        data-gamepad-region="log"
        aria-label="Kampflog"
      >
        <header className="combat-log-drawer-head">
          <h2>Kampflog</h2>
          <button type="button" className="combat-log-drawer-close" data-gamepad-dismiss onClick={onClose} aria-label="Kampflog schliessen">
            ✕
          </button>
        </header>
        <p className="section-hint combat-log-hint">
          Alle Kämpfe — kompakt; Berechnung per Zeile aufklappen.
        </p>
        <div className="combat-log-list">
          {entries.length === 0 && (
            <p className="no-content">Noch keine Kampfeinträge in diesem Spiel.</p>
          )}
          {groups.map(group => (
            <section key={group.combatId || 'unknown'} className="combat-log-fight" data-testid="combat-log-fight">
              <header className="combat-log-fight-head">
                <h3>{group.fight?.label || `Kampf #${group.combatId}`}</h3>
                <span className="combat-log-fight-meta">
                  {group.entries.length}×
                  {' · '}
                  {group.fight?.status === 'active' ? 'läuft' : 'beendet'}
                  {group.fight?.started_at ? ` · ${formatDateTime(group.fight.started_at)}` : ''}
                </span>
              </header>
              <div className="combat-log-entries">
                {group.entries.map(entry => (
                  <LogEntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </aside>
    </>
  );
}
